// QuotationManagementTableDesktop.js
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiEye, FiEdit, FiTrash2, FiCopy } from "react-icons/fi";
import {
  canEditDelete,
  isLevelOneViewingOtherQuotation,
} from "../utils/buttonVisibility";
import { createPortal } from "react-dom";

const QuotationManagementTableDesktop = ({
  quotations,
  handleSort,
  getSortIndicator,
  handleDelete,
  handleDuplicate,
}) => {
  const navigate = useNavigate();

  const getStatusStyle = (status) => {
    switch (status) {
      case "Approved":
        return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300";
      case "Pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300";
      case "Rejected":
        return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300";
      case "Canceled":
        return "bg-gray-200 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300";
    }
  };

  const truncate = (text, len = 60) =>
    text && text.length > len ? text.slice(0, len) + "..." : text;

  const Tooltip = ({ text, children }) => {
    const [coords, setCoords] = useState(null);

    const handleMouseEnter = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setCoords({
        x: rect.left,
        y: rect.bottom + 6,
        flip: window.innerHeight - rect.bottom < 100,
      });
    };

    const handleMouseLeave = () => setCoords(null);

    return (
      <span
        className="relative inline-block"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
        {coords &&
          createPortal(
            <div
              className="fixed z-[9999] bg-gray-900 text-white text-xs px-3 py-2 rounded shadow-lg max-w-[400px] whitespace-normal"
              style={{
                left: coords.x,
                top: coords.flip ? coords.y - 40 : coords.y,
              }}
            >
              {text}
            </div>,
            document.body
          )}
      </span>
    );
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm transition-colors duration-300">
      <table className="w-full text-left text-sm hidden md:table">
        <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wide">
          <tr>
            {[
              "runNumber",
              "type",
              "title",
              "client",
              "documentDate",
              "total",      // ⭐ New column
              "netAmount",
              "approvalStatus",
              "createdByUser",
            ].map((key) => (
              <th
                key={key}
                className="px-3 py-2 font-semibold cursor-pointer select-none"
                onClick={() => handleSort(key)}
              >
                {key === "runNumber"
                  ? "Run Number"
                  : key === "documentDate"
                  ? "Document Date"
                  : key === "total"
                  ? "Total Before VAT (THB)"      // ⭐ Title update
                  : key === "netAmount"
                  ? "Net Amount (THB)"            // ⭐ Fixed name
                  : key === "approvalStatus"
                  ? "Status"
                  : key === "createdByUser"
                  ? "Created By"
                  : key.charAt(0).toUpperCase() + key.slice(1)}{" "}
                {getSortIndicator(key)}
              </th>
            ))}
            <th className="px-3 py-2 font-semibold">Actions</th>
          </tr>
        </thead>

        <tbody className="dark:text-gray-200">
          {quotations.map((q, idx) => {
            const isViewOnly = isLevelOneViewingOtherQuotation(q);

            return (
              <tr
                key={q._id}
                className={`transition-colors duration-200 ${
                  idx % 2 === 0
                    ? "bg-white dark:bg-gray-900"
                    : "bg-gray-50 dark:bg-gray-800"
                } hover:bg-blue-50 dark:hover:bg-blue-900/30`}
              >
                <td className="px-3 py-2">{q.runNumber || "-"}</td>
                <td className="px-3 py-2">{q.type || "-"}</td>

                {/* ==== Title ==== */}
                <td className="px-3 py-2">
                  {q.title && q.title.length > 60 ? (
                    <Tooltip text={q.title}>
                      <span className="block max-w-[220px] truncate">
                        {truncate(q.title, 60)}
                      </span>
                    </Tooltip>
                  ) : (
                    q.title || "-"
                  )}
                </td>

                {/* ==== Client ==== */}
                <td className="px-3 py-2">
                  {q.client && q.client.length > 60 ? (
                    <Tooltip text={q.client}>
                      <span className="block max-w-[220px] truncate">
                        {truncate(q.client, 60)}
                      </span>
                    </Tooltip>
                  ) : (
                    q.client || "N/A"
                  )}
                </td>

                {/* ==== Document Date ==== */}
                <td className="px-3 py-2">
                  {q.documentDate
                    ? new Date(q.documentDate).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })
                    : "-"}
                </td>

                {/* ⭐ ==== Total Before VAT ==== */}
                <td className="px-3 py-2 text-right">
                  {q.total
                    ? q.total.toLocaleString("th-TH", {
                        style: "currency",
                        currency: "THB",
                      })
                    : "-"}
                </td>

                {/* ==== Net Amount ==== */}
                <td className="px-3 py-2 text-right">
                  {q.netAmount
                    ? q.netAmount.toLocaleString("th-TH", {
                        style: "currency",
                        currency: "THB",
                      })
                    : "-"}
                </td>

                {/* ==== Status ==== */}
                <td className="px-3 py-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(
                      q.approvalStatus
                    )}`}
                  >
                    {q.approvalStatus}
                  </span>
                </td>

                {/* ==== Created By ==== */}
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                  {q.createdByUser ? q.createdByUser.split("@")[0] : "-"}
                </td>

                {/* ==== Actions ==== */}
                <td className="px-3 py-2">
                  <div className="flex gap-2">
                    <Link
                      to={`/quotations/${q._id}`}
                      className="p-2 rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 
                               dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-800/60 
                               transition-colors"
                      title="View"
                    >
                      <FiEye size={16} />
                    </Link>

                    {!isViewOnly &&
                      q.approvalStatus !== "Canceled" &&
                      q.approvalStatus !== "Draft" && (
                        <button
                          onClick={() => handleDuplicate(q)}
                          className="p-2 rounded-md bg-purple-100 text-purple-700 hover:bg-purple-200 
                                   dark:bg-purple-900/40 dark:text-purple-300 dark:hover:bg-purple-800/60 
                                   transition-colors"
                          title="Duplicate"
                        >
                          <FiCopy size={16} />
                        </button>
                      )}

                    {!isViewOnly && canEditDelete(q.approvalStatus) && (
                      <>
                        <button
                          onClick={() => navigate(`/quotations/edit/${q._id}`)}
                          className="p-2 rounded-md bg-yellow-100 text-yellow-700 hover:bg-yellow-200 
                                   dark:bg-yellow-900/40 dark:text-yellow-300 dark:hover:bg-yellow-800/60 
                                   transition-colors"
                          title="Edit"
                        >
                          <FiEdit size={16} />
                        </button>

                        <button
                          onClick={() => handleDelete(q)}
                          className="p-2 rounded-md bg-red-100 text-red-700 hover:bg-red-200 
                                   dark:bg-red-900/40 dark:text-red-300 dark:hover:bg-red-800/60 
                                   transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default QuotationManagementTableDesktop;